import React, { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import Cookies from 'universal-cookie';
import { disableReactDevTools } from '@fvilers/disable-react-devtools';
import Store from './components/Store';
import PasswordPage from './PasswordPage';
import store from './redux_stores';
import { formURL, getPasswordConfigs } from './utils/apiUtils';
import config from 'config';

const cookies = new Cookies();
const skipPasswordCheck = cookies.get('obsess-dev-cookie');
let passwordButtonCopy;
let passwordBodyText;
let passwordPageStyle;

if (process.env.NODE_ENV === 'production') {
	disableReactDevTools();
}

/**
 * @module App
 *
 * @description
 * This is where the store setup starts.
 *
 * This component checks if the store has a password
 * - If the store has a password, it renders the [`PasswordPage`]{@link module:PasswordPage}.
 * - If the store does not have a password, it continues with rendering the store.
 *
 *
 * @return {*}
 */
const App = () => {
	const [passwordEnabled, setPasswordEnabled] = useState(undefined);

	/**
	 * @member checkPassword
	 * @description
	 * - Get Password Config from Web Store API
	 * - Check if password is enabled from AVA
	 * - If the password is enabled, it will check if `obsess-dev-cookie`. This is the cookie that we use to track if the user has entered password for this store in the past.
	 * - Show [`PasswordPage`]{@link module:PasswordPage} if password is enabled and cookie is not present.
	 * - Else proceed with rendering the store.
	 * @return {Promise<void>}
	 */
	const checkPassword = async () => {
		const {
			password_enabled: enabled,
			background_image: image,
			submit_button_copy: text,
			message = '',
			show_on_environments,
		} = await getPasswordConfigs();

		if (!enabled || skipPasswordCheck) {
			setPasswordEnabled(false);
			return;
		}

		// show_on_environments possible values are ['prod', 'beta', 'client'] or empty array
		if (
			show_on_environments &&
			!show_on_environments.includes(config.ENV)
		) {
			setPasswordEnabled(false);
			return;
		}

		passwordPageStyle = image && {
			background: `url(${formURL(image)})`,
		};
		passwordBodyText = message;
		passwordButtonCopy = text;

		setPasswordEnabled(true);
	};

	/**
	 * @member renderStore
	 *
	 * @description
	 * - Do not render anything till password config has been fetched.
	 * - If passwordEnabled - show password page.
	 * - Else render store.
	 * @return {JSX.Element|null}
	 *
	 */
	const renderStore = () => {
		if (passwordEnabled === undefined) {
			return null;
		}
		if (passwordEnabled) {
			return (
				<PasswordPage
					mode={'store'}
					setPasswordEnabled={setPasswordEnabled}
					passwordPageStyle={passwordPageStyle}
					body={passwordBodyText}
					passwordButtonCopy={passwordButtonCopy}
				/>
			);
		}

		return (
			<Provider store={store}>
				<Store />
			</Provider>
		);
	};

	/**
	 * @member triggerPasswordCheck
	 *
	 * @desc
	 * - Triggers password check on initial mount
	 */
	useEffect(() => {
		checkPassword();
	}, []);

	return renderStore();
};

export default App;
